/**
 * Режим настройки: рамка по границам источника и его размер.
 *
 * ЗАЧЕМ. Трансформацию источника в сцене OBS изнутри страницы узнать
 * нельзя вовсе — сдвинут ли он, обрезан ли, растянут ли мышью. Это
 * умеет только obs-websocket, отложенный бессрочно (docs/06).
 * Несовпадение при этом тихое: модератор ставит вещь в угол кадра,
 * а в эфире она оказывается посреди экрана, и понять, где ошибка,
 * невозможно — панель показывает одно, зритель видит другое.
 *
 * Режим настройки не даёт знания о трансформации. Он делает
 * несовпадение ВИДИМЫМ: рамка рисуется ровно по границам страницы,
 * и человек глазами сверяет её с кадром в предпросмотре OBS.
 * Совпала — источник настроен верно; сдвинута или обрезана — видно
 * сразу и видно как.
 *
 * РАМКУ ВИДЯТ ЗРИТЕЛИ. Оверлей в эфире, и включённый режим означает
 * синюю рамку поверх картинки для всех, кто смотрит. Поэтому он
 * гаснет сам: забытый включённым режим — не гипотеза, а то, чем
 * такие вещи заканчиваются.
 */

/** Через сколько режим гаснет сам. */
import { decodeMessage } from '@owm/scene';

export const SETUP_TIMEOUT_MS = 5 * 60 * 1000;

export interface SetupOptions {
  /** Подменяется в тестах: ждать пять минут в тесте нечего. */
  readonly setTimer?: (fn: () => void, ms: number) => number;
  readonly clearTimer?: (handle: number) => void;
  readonly timeout?: number;
}

export interface SetupFrame {
  /** Включает режим и показывает размеры. */
  readonly show: (sizes: SetupSizes) => void;
  readonly hide: () => void;
  /** Обновляет подпись, если режим включён. Иначе ничего. */
  readonly update: (sizes: SetupSizes) => void;
  readonly visible: () => boolean;
}

/** Что написать на рамке. */
export interface SetupSizes {
  /** Размер страницы — он же размер Browser Source. */
  readonly source: { readonly width: number; readonly height: number };
  /** Размер кадра сцены. Ноль, если сцена ещё не пришла. */
  readonly canvas: { readonly width: number; readonly height: number };
}

/**
 * Создаёт рамку настройки.
 *
 * В обычном режиме её нет в DOM ВОВСЕ, а не спрятана стилем.
 * Спрятанный узел однажды покажется от чужого правила стилей —
 * и покажется он зрителям.
 */
export function createSetupFrame(root: HTMLElement, options: SetupOptions = {}): SetupFrame {
  const setTimer = options.setTimer ?? ((fn, ms) => window.setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((handle) => window.clearTimeout(handle));
  const timeout = options.timeout ?? SETUP_TIMEOUT_MS;

  let parts: { frame: HTMLElement; label: HTMLElement } | null = null;
  let timer: number | null = null;

  function stopTimer(): void {
    if (timer === null) return;

    clearTimer(timer);
    timer = null;
  }

  function hide(): void {
    stopTimer();

    parts?.frame.remove();
    parts = null;
  }

  return {
    show(sizes) {
      if (parts === null) {
        parts = build();
        root.append(parts.frame);
      }

      parts.label.textContent = describe(sizes);

      // Отсчёт начинается заново на каждом включении: повторная
      // команда из панели — это «я всё ещё настраиваю»
      stopTimer();
      timer = setTimer(hide, timeout);
    },

    update(sizes) {
      if (parts === null) return;

      parts.label.textContent = describe(sizes);
    },

    hide,

    visible() {
      return parts !== null;
    },
  };
}

/**
 * Строит рамку и подпись.
 *
 * Возвращает оба узла, а не ищет подпись потом запросом: найденное
 * запросом бывает null, и на этот null пришлось бы отвечать в двух
 * местах — ответом, который никогда не понадобится.
 *
 * Толщина в пикселях страницы, а не в единицах сцены: рамка
 * показывает границы ИСТОЧНИКА, и масштабироваться вместе со сценой
 * ей нечего — на мелком источнике она бы истончилась до невидимой.
 */
function build(): { frame: HTMLElement; label: HTMLElement } {
  const frame = document.createElement('div');
  frame.dataset.role = 'setup-frame';
  frame.style.position = 'absolute';
  frame.style.inset = '0';
  frame.style.border = '3px solid #58a6ff';
  // Рамка не должна ловить мышь: оверлей открывают и в браузере,
  // чтобы проверить, и перехваченный клик там сбивает с толку
  frame.style.pointerEvents = 'none';
  // Поверх всех слоёв: она и нужна затем, чтобы её было видно
  frame.style.zIndex = '2147483647';

  const size = document.createElement('div');
  size.dataset.role = 'setup-size';
  size.style.position = 'absolute';
  size.style.left = '8px';
  size.style.top = '8px';
  size.style.padding = '4px 8px';
  size.style.borderRadius = '4px';
  // Подложка обязательна: белый текст на светлой картинке стримера
  // не читается, а прочитать его — единственный смысл подписи
  size.style.background = 'rgba(1, 4, 9, 0.75)';
  size.style.font = '14px/1.4 system-ui, sans-serif';
  size.style.color = '#fff';

  frame.append(size);

  return { frame, label: size };
}

/**
 * Что написать на рамке.
 *
 * Кадр называется, только когда известен: «0×0» на подписи выглядит
 * как поломка оверлея, а означает лишь, что сцена ещё не пришла.
 */
function describe(sizes: SetupSizes): string {
  const source = `Источник ${sizes.source.width}×${sizes.source.height}`;

  return sizes.canvas.width > 0 && sizes.canvas.height > 0
    ? `${source}, кадр ${sizes.canvas.width}×${sizes.canvas.height}`
    : source;
}

/** Разбирает команду режима настройки. Не она — null. */
export function setupCommand(raw: unknown): boolean | null {
  const parsed = decodeMessage(raw);
  if (parsed === null) return null;

  const message = parsed as { t?: unknown; on?: unknown };
  if (message.t !== 'setup') return null;

  // Команда без внятного «включить или выключить» — расхождение
  // версий панели и оверлея. Гадать нельзя: угадаем «включить» —
  // покажем рамку зрителям без спроса
  return typeof message.on === 'boolean' ? message.on : null;
}

/**
 * Разбирает просьбу пересоздать слой.
 *
 * Живёт рядом с командой режима настройки, потому что это того же
 * рода сообщение: не изменение сцены, а команда самому оверлею.
 * Возвращает null для всего остального — вызывающий по этому
 * понимает, что сообщение не про него.
 */
export function reloadCommand(raw: unknown): string | null {
  const parsed = decodeMessage(raw);
  if (parsed === null) return null;

  const message = parsed as { t?: unknown; layerId?: unknown };
  if (message.t !== 'reload') return null;

  // Просьба без слоя — расхождение версий панели и оверлея.
  // Перезагружать «что-нибудь» нельзя: перезагрузка видна зрителям
  return typeof message.layerId === 'string' && message.layerId !== '' ? message.layerId : null;
}
